import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CalendarClock, ClipboardCheck, Plus, ShoppingCart, Truck } from 'lucide-react';
import { DataTable, type Column } from '@/ui/DataTable';
import { ChipGroup, FilterChip, Pagination } from '@/ui/Filters';
import { Badge, Code } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { SearchInput, Select } from '@/ui/Field';
import {
  Detail,
  DetailList,
  EmptyState,
  ErrorState,
  Page,
  PageBody,
  PageHeader,
  Panel,
  Skeleton,
  Toolbar,
} from '@/ui/Panel';
import { SplitView } from '@/ui/SplitView';
import { useShortcutHint } from '@/app/StatusLine';
import { errorMessage } from '@/lib/api/client';
import { dateTime, humanize, money, num, phone, qty, smartDate } from '@/lib/format';
import type { OrderSummary } from '@/lib/api/types';
import {
  OPEN_STATUSES,
  customerLabel,
  fetchOrder,
  fetchOrders,
  orderUrgency,
  ordersKey,
  pickProgress,
  urgencyTone,
} from '@/features/orders/lib';
import {
  DeliveryBadge,
  LineStatusBadge,
  OrderStatusBadge,
  StatusTimeline,
} from '@/features/orders/chrome';
import { useDebounced } from '@/features/orders/SearchPicker';
import { asText } from '@/lib/searchParams';

/* Order triage.
   The list is a queue, not an archive: the default view is what still needs a
   human, rows carry an urgency rail driven by `requestedFor`, and selecting a
   row never navigates — the inspector answers "what is this order?" while
   `/orders/$id` is where you actually work it. */

const PAGE_SIZE = 50;

/** Chip → the `status` the API filters on. `submitted` is the open queue. */
const CHIPS = [
  { value: 'all', label: 'All', status: undefined },
  { value: 'open', label: 'Open', status: 'submitted' },
  { value: 'picking', label: 'Picking', status: 'picking' },
  { value: 'ready', label: 'Ready', status: 'picked' },
  { value: 'done', label: 'Done', status: 'completed' },
  { value: 'cancelled', label: 'Cancelled', status: 'cancelled' },
  { value: 'draft', label: 'Draft', status: 'draft' },
] as const;

type ChipValue = (typeof CHIPS)[number]['value'];

const CHIP_VALUES = CHIPS.map((c) => c.value);

/** Every field is optional so any screen can `Link to="/orders"` without
 *  restating the whole filter set; `validateSearch` always fills them in. */
interface OrdersSearch {
  id?: number;
  status?: ChipValue;
  q?: string;
  fulfillment?: '' | 'pickup' | 'delivery';
  from?: string;
  to?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  page?: number;
}

export const Route = createFileRoute('/_app/orders')({
  staticData: { crumb: 'Orders' },
  validateSearch: (s: Record<string, unknown>): OrdersSearch => ({
    id: Number(s.id) > 0 ? Number(s.id) : undefined,
    status: CHIP_VALUES.includes(s.status as ChipValue)
      ? (s.status as ChipValue)
      : 'open',
    q: asText(s.q, ''),
    fulfillment:
      s.fulfillment === 'pickup' || s.fulfillment === 'delivery' ? s.fulfillment : '',
    from: asText(s.from, ''),
    to: asText(s.to, ''),
    sort: asText(s.sort, 'created_at'),
    order: s.order === 'asc' ? 'asc' : 'desc',
    page: Number(s.page) > 0 ? Number(s.page) : 1,
  }),
  component: OrdersScreen,
});

function OrdersScreen() {
  const raw = Route.useSearch();
  const navigate = Route.useNavigate();

  // `validateSearch` normalises every field; the optional types exist only so
  // other screens can link here without restating the filter set.
  const search = {
    id: raw.id,
    status: raw.status ?? 'open',
    q: raw.q ?? '',
    fulfillment: raw.fulfillment ?? '',
    from: raw.from ?? '',
    to: raw.to ?? '',
    sort: raw.sort ?? 'created_at',
    order: raw.order ?? 'desc',
    page: raw.page ?? 1,
  };

  const [rawQuery, setRawQuery] = useState(search.q);
  const debouncedQuery = useDebounced(rawQuery, 220);

  useShortcutHint([
    { keys: 'j / k', label: 'Move' },
    { keys: 'Enter', label: 'Inspect' },
    { keys: 'o', label: 'Open order' },
    { keys: 'n', label: 'New order' },
  ]);

  function patch(next: Partial<OrdersSearch>) {
    void navigate({ search: (prev: OrdersSearch) => ({ ...prev, ...next }) });
  }

  useEffect(() => {
    if (debouncedQuery !== search.q) {
      void navigate({
        search: (prev: OrdersSearch) => ({ ...prev, q: debouncedQuery, page: 1 }),
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  const chip = CHIPS.find((c) => c.value === search.status) ?? CHIPS[0];

  // Shared by the list and the per-chip counters so a count always describes
  // the same slice the list is showing.
  const baseParams = {
    search: search.q || undefined,
    fulfillmentType: search.fulfillment || undefined,
    from: search.from || undefined,
    to: search.to || undefined,
  };

  const list = useQuery({
    queryKey: ordersKey.list({ ...baseParams, ...search }),
    queryFn: () =>
      fetchOrders({
        ...baseParams,
        status: chip.status,
        sort: search.sort,
        order: search.order,
        page: search.page,
        pageSize: PAGE_SIZE,
      }),
    staleTime: 10_000,
  });

  // Real counts per chip. One query so the chip row can never show a
  // half-updated set, and every leg carries the same filters as the list.
  const counts = useQuery({
    queryKey: ordersKey.counts(baseParams),
    staleTime: 30_000,
    queryFn: async () => {
      const entries = await Promise.all(
        CHIPS.map(async (c) => {
          const res = await fetchOrders({ ...baseParams, status: c.status, pageSize: 1 });
          return [c.value, res.total ?? 0] as const;
        })
      );
      return Object.fromEntries(entries) as Record<ChipValue, number>;
    },
  });

  // Urgency is a property of the whole open queue, not of the visible page —
  // an operator asking "how late am I?" means everything still unfinished.
  const openQueue = useQuery({
    queryKey: ordersKey.urgency(),
    staleTime: 30_000,
    queryFn: () =>
      fetchOrders({
        status: OPEN_STATUSES.join(','),
        pageSize: 200,
        sort: 'created_at',
        order: 'asc',
      }),
  });

  const openRows = openQueue.data?.data ?? [];
  const overdue = openRows.filter((o) => orderUrgency(o) === 'overdue').length;
  const dueSoon = openRows.filter((o) => orderUrgency(o) === 'soon').length;
  // The queue read is capped at one page; say so rather than under-report.
  const capped = (openQueue.data?.total ?? 0) > openRows.length;
  const suffix = capped ? '+' : '';

  const rows = list.data?.data;
  const total = list.data?.total ?? 0;

  const selectedId = search.id;

  // `o` opens the workflow hub for whatever is in the inspector. Selection
  // itself never navigates (DESIGN.md), so opening stays an explicit act.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'o' && selectedId) {
        e.preventDefault();
        void navigate({ to: '/orders/$id', params: { id: String(selectedId) } });
      } else if (e.key === 'n') {
        e.preventDefault();
        void navigate({ to: '/orders/new' });
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedId, navigate]);

  const columns: Array<Column<OrderSummary>> = [
    {
      id: 'order_number',
      header: 'Order',
      width: '8.5rem',
      sortable: true,
      cell: (row) => (
        <span className="flex items-center gap-1.5">
          <span className="text-ink font-mono text-xs">{row.orderNumber}</span>
          {row.source !== 'phone' ? (
            <Badge tone="neutral">{humanize(row.source)}</Badge>
          ) : null}
        </span>
      ),
    },
    {
      // The name is what an operator scans for, so it takes the flexible track
      // and the phone gets a fixed one of its own. Sharing a cell let the
      // fixed-width phone squeeze the name down to a single letter.
      id: 'customer',
      header: 'Customer',
      cell: (row) => <span className="text-ink truncate">{customerLabel(row)}</span>,
    },
    {
      id: 'phone',
      header: 'Phone',
      width: '7rem',
      mono: true,
      hideBelow: 'lg',
      cell: (row) => (
        <span className="text-2xs text-ink-faint">
          {row.customerPhone ? phone(row.customerPhone) : '—'}
        </span>
      ),
    },
    {
      id: 'fulfillmentType',
      header: 'Type',
      width: '5.5rem',
      hideBelow: 'lg',
      cell: (row) => (
        <span className="text-ink-muted flex items-center gap-1 text-xs">
          {row.fulfillmentType === 'delivery' ? (
            <Truck className="size-3 shrink-0" />
          ) : (
            <ShoppingCart className="size-3 shrink-0" />
          )}
          {humanize(row.fulfillmentType)}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '6rem',
      sortable: true,
      cell: (row) => <OrderStatusBadge status={row.status} />,
    },
    {
      id: 'items',
      header: 'Lines',
      width: '3.5rem',
      align: 'right',
      mono: true,
      hideBelow: 'lg',
      cell: (row) => num(row.itemCount),
    },
    {
      id: 'created_at',
      header: 'Placed',
      width: '6rem',
      sortable: true,
      hideBelow: '2xl',
      mono: true,
      cell: (row) => (
        <span className="text-ink-muted text-xs">{smartDate(row.createdAt)}</span>
      ),
    },
    {
      id: 'requestedFor',
      header: 'Due',
      width: '6.5rem',
      mono: true,
      cell: (row) => {
        const u = orderUrgency(row);
        return (
          <span
            className={
              u === 'overdue'
                ? 'text-negative text-xs'
                : u === 'soon'
                  ? 'text-warn text-xs'
                  : 'text-ink-muted text-xs'
            }
            title={row.requestedFor ? dateTime(row.requestedFor) : undefined}
          >
            {row.requestedFor ? smartDate(row.requestedFor) : '—'}
          </span>
        );
      },
    },
    {
      id: 'estimated_total',
      header: 'Total',
      width: '6rem',
      align: 'right',
      mono: true,
      sortable: true,
      cell: (row) => (
        <span className="text-ink">
          {money(row.actualTotalCents ?? row.estimatedTotalCents)}
        </span>
      ),
    },
  ];

  return (
    <Page>
      <PageHeader
        eyebrow="Selling"
        title="Orders"
        subtitle={
          openQueue.isLoading
            ? 'Counting the queue…'
            : `${num(overdue)}${suffix} overdue · ${num(dueSoon)}${suffix} due soon · ${num(total)} in this view`
        }
        actions={
          <Button
            variant="primary"
            size="sm"
            icon={<Plus className="size-3.5" />}
            kbd="n"
            onClick={() => void navigate({ to: '/orders/new' })}
          >
            New order
          </Button>
        }
      />

      <Toolbar>
        <ChipGroup<ChipValue>
          value={search.status}
          onChange={(value) => patch({ status: value, page: 1 })}
          options={CHIPS.map((c) => ({
            value: c.value,
            label: c.label,
            count: counts.data?.[c.value],
            ...(c.value === 'picking' ? { tone: 'warn' as const } : {}),
          }))}
        />

        <div className="ml-auto flex items-center gap-2">
          <SearchInput
            value={rawQuery}
            onValueChange={setRawQuery}
            placeholder="Order #, name or phone"
            size="sm"
            className="w-56"
          />
          <Select
            size="sm"
            value={search.fulfillment}
            onChange={(e) =>
              patch({
                fulfillment: e.currentTarget.value as OrdersSearch['fulfillment'],
                page: 1,
              })
            }
            className="w-32"
            aria-label="Fulfillment type"
          >
            <option value="">Any type</option>
            <option value="pickup">Pickup</option>
            <option value="delivery">Delivery</option>
          </Select>
        </div>
      </Toolbar>

      {search.from || search.to ? (
        <div className="border-line flex shrink-0 items-center gap-2 border-b px-4 py-1.5">
          {search.from ? (
            <FilterChip
              label="Placed from"
              value={search.from}
              onRemove={() => patch({ from: '', page: 1 })}
            />
          ) : null}
          {search.to ? (
            <FilterChip
              label="Placed to"
              value={search.to}
              onRemove={() => patch({ to: '', page: 1 })}
            />
          ) : null}
        </div>
      ) : null}

      <PageBody bleed className="min-h-0 overflow-hidden">
        <SplitView
          storageKey="orders"
          onCloseDetail={() => patch({ id: undefined })}
          placeholder={
            <EmptyState
              icon={<ClipboardCheck />}
              title="No order selected"
              description="Pick a row to see its lines, payment and history. Press o to open the full workflow."
              compact
            />
          }
          detail={selectedId ? <OrderInspector id={selectedId} /> : undefined}
          list={
            <DataTable
              rows={rows}
              columns={columns}
              rowKey={(row) => row.id}
              loading={list.isLoading}
              error={list.error}
              onRetry={() => void list.refetch()}
              onRowOpen={(row) => patch({ id: row.id })}
              isRowActive={(row) => row.id === selectedId}
              rowTone={(row) => urgencyTone(orderUrgency(row))}
              sort={{
                by: search.sort,
                dir: search.order,
                onChange: (by, dir) => patch({ sort: by, order: dir, page: 1 }),
              }}
              empty={{
                icon: <ShoppingCart />,
                title:
                  search.q || search.fulfillment
                    ? 'No orders match those filters'
                    : `Nothing in ${chip.label.toLowerCase()}`,
                description:
                  search.status === 'open'
                    ? 'The open queue is clear. Nice.'
                    : 'Try another status chip or clear the search.',
              }}
              footer={
                <Pagination
                  page={search.page}
                  pageSize={PAGE_SIZE}
                  total={total}
                  onPageChange={(page) => patch({ page })}
                  className="w-full"
                />
              }
            />
          }
        />
      </PageBody>
    </Page>
  );
}

/* ── Inspector ────────────────────────────────────────────────────────────── */

function OrderInspector({ id }: { id: number }) {
  const navigate = Route.useNavigate();
  const detail = useQuery({
    queryKey: ordersKey.detail(id),
    queryFn: () => fetchOrder(id),
    staleTime: 10_000,
  });

  if (detail.isLoading) {
    return (
      <div className="space-y-3 p-3">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-3 w-56" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (detail.error || !detail.data) {
    return (
      <ErrorState
        title="Could not load this order"
        message={errorMessage(detail.error)}
        onRetry={() => void detail.refetch()}
        className="m-3"
      />
    );
  }

  const order = detail.data;
  const progress = pickProgress(order.items);
  const pct = progress.total === 0 ? 0 : (progress.resolved / progress.total) * 100;
  const urgency = orderUrgency(order);

  return (
    <div className="space-y-3 p-3">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-ink font-mono text-sm">{order.orderNumber}</span>
            <OrderStatusBadge status={order.status} dot />
          </div>
          <div className="text-ink-muted mt-0.5 truncate text-xs">
            {customerLabel(order)}
            {order.customerPhone ? ` · ${phone(order.customerPhone)}` : ''}
          </div>
        </div>
        <Button
          size="sm"
          kbd="o"
          onClick={() =>
            void navigate({ to: '/orders/$id', params: { id: String(order.id) } })
          }
        >
          Open
        </Button>
      </div>

      {urgency ? (
        <div
          className={
            urgency === 'overdue'
              ? 'border-negative/40 bg-negative-muted text-ink flex items-center gap-2 rounded border px-2.5 py-1.5 text-xs'
              : 'border-warn/40 bg-warn-muted text-ink flex items-center gap-2 rounded border px-2.5 py-1.5 text-xs'
          }
        >
          <CalendarClock className="size-3.5 shrink-0" />
          {urgency === 'overdue' ? 'Past its requested time' : 'Due soon'} —{' '}
          {dateTime(order.requestedFor)}
        </div>
      ) : null}

      {/* Fulfillment progress */}
      <Panel title="Fulfillment">
        <div className="flex items-baseline justify-between text-xs">
          <span className="text-ink-muted">Lines resolved</span>
          <span className="text-ink font-mono tabular-nums">
            {progress.resolved} / {progress.total}
          </span>
        </div>
        <div className="bg-surface-sunken mt-1.5 h-1 w-full overflow-hidden rounded-xs">
          <div
            className={pct === 100 ? 'bg-positive h-full' : 'bg-signal h-full'}
            style={{ width: `${pct}%` }}
          />
        </div>
        <DetailList columns={2} className="mt-2">
          <Detail label="Type">{humanize(order.fulfillmentType)}</Detail>
          <Detail label="Source">{humanize(order.source)}</Detail>
          <Detail label="Requested for">
            {order.requestedFor ? dateTime(order.requestedFor) : 'No time set'}
          </Detail>
          <Detail label="Substitutions">
            {order.substitutionPref
              ? humanize(order.substitutionPref)
              : 'Customer default'}
          </Detail>
        </DetailList>
      </Panel>

      {/* Lines */}
      <Panel title="Lines" description={`${progress.total} on this order`} bleed>
        <div className="divide-line-soft divide-y">
          {order.items.map((item) => (
            <div key={item.id} className="flex items-start gap-2 px-3 py-1.5">
              <div className="min-w-0 flex-1">
                <div className="text-ink truncate text-xs">
                  {item.productNameSnapshot}
                </div>
                <div className="text-2xs text-ink-faint mt-0.5 flex items-center gap-2">
                  <span className="font-mono tabular-nums">
                    {qty(item.orderedQty)}
                    {item.pickedQty !== null ? ` → ${qty(item.pickedQty)}` : ''}
                  </span>
                  {item.actualWeightLbs ? (
                    <span className="font-mono tabular-nums">
                      {qty(item.actualWeightLbs)} lb
                    </span>
                  ) : null}
                  {item.substitutedProductName ? (
                    <span className="truncate">→ {item.substitutedProductName}</span>
                  ) : null}
                  {item.zoneName ? <Code>{item.zoneName}</Code> : null}
                </div>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-0.5">
                <span className="text-ink font-mono text-xs tabular-nums">
                  {money(item.actualExtPriceCents ?? item.estimatedExtPriceCents)}
                </span>
                <LineStatusBadge status={item.lineStatus} />
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {/* Payment */}
      <Panel title="Payment">
        <DetailList columns={2}>
          <Detail label="Method">{humanize(order.paymentMethod)}</Detail>
          <Detail label="Payment status">
            {order.paymentStatus ? humanize(order.paymentStatus) : 'Not charged'}
          </Detail>
          <Detail label="Estimated" mono>
            {money(order.estimatedTotalCents)}
          </Detail>
          <Detail label="Actual" mono>
            {order.actualTotalCents === null
              ? 'Not picked'
              : money(order.actualTotalCents)}
          </Detail>
          {order.deliveryFeeCents ? (
            <Detail label="Delivery fee" mono>
              {money(order.deliveryFeeCents)}
            </Detail>
          ) : null}
          {order.tipCents ? (
            <Detail label="Tip" mono>
              {money(order.tipCents)}
            </Detail>
          ) : null}
          {order.paymentRefNumber ? (
            <Detail label="Gateway ref" mono>
              {order.paymentRefNumber}
            </Detail>
          ) : null}
          {order.transactionId ? (
            <Detail label="Transaction" mono>
              <Code copyable>{order.transactionId}</Code>
            </Detail>
          ) : null}
        </DetailList>
        {order.paymentError ? (
          <div className="border-negative/30 bg-negative-muted text-2xs text-ink mt-2 rounded border p-2 font-mono">
            {order.paymentError}
          </div>
        ) : null}
      </Panel>

      {order.campaignId ? (
        <Panel title="Campaign">
          <div className="text-ink text-xs">
            {order.campaignName ?? `#${order.campaignId}`}
          </div>
        </Panel>
      ) : null}

      {order.fulfillmentType === 'delivery' ? (
        <Panel title="Delivery" actions={<DeliveryBadge status={order.deliveryStatus} />}>
          <div className="text-ink space-y-0.5 text-xs">
            <div>{order.deliveryName || customerLabel(order)}</div>
            {order.deliveryPhone ? (
              <div className="text-2xs text-ink-muted font-mono">
                {phone(order.deliveryPhone)}
              </div>
            ) : null}
            <div className="text-ink-muted">
              {order.deliveryAddress1 ?? '—'}
              {order.deliveryAddress2 ? `, ${order.deliveryAddress2}` : ''}
            </div>
            <div className="text-ink-muted">
              {[order.deliveryCity, order.deliveryState, order.deliveryPostalCode]
                .filter(Boolean)
                .join(', ') || '—'}
            </div>
            {order.deliveryInstructions ? (
              <div className="border-line bg-surface-sunken text-2xs text-ink-muted mt-1.5 rounded border p-2">
                {order.deliveryInstructions}
              </div>
            ) : null}
            {order.deliveryFailureReason ? (
              <div className="border-negative/30 bg-negative-muted text-2xs text-ink mt-1.5 rounded border p-2">
                {order.deliveryFailureReason}
              </div>
            ) : null}
          </div>
        </Panel>
      ) : null}

      {order.note ? (
        <Panel title="Note">
          <p className="text-ink text-xs leading-relaxed whitespace-pre-wrap">
            {order.note}
          </p>
        </Panel>
      ) : null}

      {order.status === 'cancelled' ? (
        <Panel title="Cancellation">
          <p className="text-ink text-xs">
            {order.cancelledReason || 'No reason was recorded.'}
          </p>
        </Panel>
      ) : null}

      <Panel title="History" bleed>
        <StatusTimeline entries={order.statusLog} />
      </Panel>
    </div>
  );
}
